import { useState, useEffect, useCallback } from 'react'
import { useMsal } from '@azure/msal-react'
import { claimInvitation } from '../api/client'
import { ApiError } from '../api/types'

const INVITATION_TOKEN_KEY = 'equivolt_pending_invitation_token'

interface Props {
  email: string | null
  onSignOut: () => void
  onClaimSuccess: () => void  // called after successful claim so dashboard reloads
}

type SubmitState =
  | { state: 'idle' }
  | { state: 'submitting' }
  | { state: 'error'; message: string }

export function NotLinked({ email, onSignOut, onClaimSuccess }: Props) {
  const { instance } = useMsal()
  const [tokenInput, setTokenInput] = useState<string>(() => {
    return sessionStorage.getItem(INVITATION_TOKEN_KEY) || ''
  })
  const [submitState, setSubmitState] = useState<SubmitState>({ state: 'idle' })
  const [autoAttempted, setAutoAttempted] = useState(false)

  const submit = useCallback(async (token: string) => {
    setSubmitState({ state: 'submitting' })
    try {
      await claimInvitation(instance, token.trim())
      // Success — clear the pending token and tell parent to reload
      sessionStorage.removeItem(INVITATION_TOKEN_KEY)
      onClaimSuccess()
    } catch (err) {
      const apiError = err as ApiError
      setSubmitState({
        state: 'error',
        message: apiError.message || 'Something went wrong. Please try again.',
      })
    }
  }, [instance, onClaimSuccess])

  // Auto-submit if we arrived with a token from the URL (via sessionStorage)
  useEffect(() => {
    const pending = sessionStorage.getItem(INVITATION_TOKEN_KEY)
    if (pending && !autoAttempted && submitState.state === 'idle') {
      setAutoAttempted(true)
      submit(pending)
    }
  }, [autoAttempted, submitState.state, submit])

  const handleManualSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (submitState.state === 'submitting') return
    if (!tokenInput.trim()) return
    submit(tokenInput)
  }

  const isSubmitting = submitState.state === 'submitting'

  return (
    <div className="claim-screen">
      <h1>Welcome to Equivolt Portal</h1>

      <p>
        You're signed in{email && <> as <strong>{email}</strong></>}. To view
        your battery system, enter the invitation code we sent you.
      </p>

      <form onSubmit={handleManualSubmit} className="claim-form">
        <label htmlFor="invitation-token">Invitation code</label>
        <input
          id="invitation-token"
          type="text"
          value={tokenInput}
          onChange={(e) => setTokenInput(e.target.value)}
          placeholder="EQV-..."
          disabled={isSubmitting}
          autoComplete="off"
          spellCheck={false}
          autoCapitalize="off"
          autoCorrect="off"
        />
        <button type="submit" disabled={isSubmitting || !tokenInput.trim()}>
          {isSubmitting ? 'Verifying…' : 'Continue'}
        </button>
      </form>

      {submitState.state === 'error' && (
        <div className="claim-error" role="alert">
          {submitState.message}
        </div>
      )}

      <p className="claim-help">
        Don't have an invitation? Contact your Equivolt installer or support to
        be onboarded.
      </p>

      <div className="button-row">
        <button onClick={onSignOut} className="btn-secondary">Sign out</button>
      </div>
    </div>
  )
}
